// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// Maps a chat answer's data "source" (backend-produced, route-agnostic — see
// chat/sources.py) to the actual dashboard view. The dashboard has no URL scheme
// for views: which one shows is driven by Redux `ui_state.active_tiles`. So a
// "source" resolves to a tile id (+ optional workspace controls), and navigation
// is a Redux dispatch, not a route change.
//
// Phase 1: switch to the right tile (+ booking-curve DBA scope) and minimize the
// chat. Date-range / segmentation sync is Phase 2.

export const SOURCE_REGISTRY = {
  // OTB: hotel grain uses the "otb" tile; any segmented grain uses the dedicated
  // "otb_v2_multi_segment" tile (which reads workspace_controls.segmentType).
  otb:               { tileId: "otb", segmentedTileId: "otb_v2_multi_segment", label: "On-the-Books" },
  pickup:            { tileId: "pickup",                label: "Pickup" },
  booking_curve:     { tileId: "pace",                  label: "Booking Curve", scopeType: "Dba" },
  // Sell rates: land on OTB (SellRate measure lives there); revisit if a dedicated
  // market_sellrate_report landing is preferred.
  sell_rates:        { tileId: "otb",                   label: "Sell Rates" },
  // NO `room_type_pricing` ENTRY, DELIBERATELY. There is no room-type pricing
  // screen — there is ONE Pricing screen and room type is a filter on it — so
  // the backend maps get_room_type_pricing to the `recommendations` domain and
  // the chip reads "Pricing Recommendations". Don't re-add it: three targets were
  // tried and all were wrong (a data-source id → white screen; `room_pricing`,
  // a real TILES key belonging to a product variant most users don't have →
  // "not enabled for you"; then the Pricing tile plus a toggle, which was
  // right-ish but advertised a view that doesn't exist).
  forecast:          { tileId: "forecast",              label: "Forecast" },
  recommendations:   { tileId: "pricing",               label: "Pricing Recommendations" },
  market:            { tileId: "market",                label: "Market Demand" },
  compset:           { tileId: "compset",               label: "Competitive Set" },
  // Distribution push-status views (status tiles; not part of an IntelliMerge).
  rate_push:         { tileId: "DailyRatesendStatus",     label: "Rate Push Status" },
  restriction_push:  { tileId: "restrictions_push_status", label: "Restriction Push Status" },
  // 1-year monthly summary views (own tiles; not mergeable).
  hotel_summary:     { tileId: "hotel_summary",           label: "Hotel Summary" },
  market_summary:    { tileId: "market_summary",          label: "Market Summary" },
  // Uploaded budget targets (Budget tile).
  budget:            { tileId: "budget",                  label: "Budget" },
  bi_sensitivity:    { tileId: "bi_sensitivity_analysis", label: "Sensitivity Analysis" },
  // Reports-menu tiles. Opening a report sets active_tiles like any other tile, so
  // these resolve exactly the same way — a report is a view, not a separate surface.
  pace_segment:      { tileId: "bi_pace_comparison",      label: "Pace by Segment" },
  revpar_index:      { tileId: "revpar_index",            label: "RevPAR Performance" },
  // Saved group PROPOSALS (the report tile). Distinct from `group_iq` below, which
  // is the quoting tool itself and navigates to its own page.
  group_quotes:      { tileId: "group_iq_reports",        label: "Group Proposals" },
  // GroupIQ is a PAGE (route), not a tile: `route` entries navigate instead of
  // switching tiles (see navigateToSource). newTab matches the main app, which
  // opens GroupIQ in its own tab. Not part of IntelliMerge.
  group_iq:          { route: "/groupIQ", newTab: true,   label: "GroupIQ", prefillParams: true },
};

// grain (from backend params) -> workspace_controls.segmentType (used in Phase 2).
export const GRAIN_TO_SEGMENT = {
  hotel: "hotel",
  market_segment: "market_segment",
  room_type: "room_type",
  rate_plan: "rate_plan",
};

export function registryFor(source) {
  return (source && source.domain && SOURCE_REGISTRY[source.domain]) || null;
}

// Which dashboard tile a domain contributes to an IntelliMerge (combined) view.
// Only the tiles the app marks `mergeable` are listed.
export const DOMAIN_MERGE_TILE = {
  otb: "otb",
  pickup: "pickup",
  forecast: "forecast",
  market: "market",
  booking_curve: "pace",
  recommendations: "pricing",
  sell_rates: "otb",
  compset: "compset",
};

// Build the ordered, de-duplicated tile set for a combined view from an answer's
// sources. Mirrors onTileMergeHandler's ordering rule: `pricing` always goes last
// (it's the merge anchor-last tile). Capped so the merged view stays readable.
export function mergeTilesForSources(sources) {
  const order = [];
  (sources || []).forEach((s) => {
    const t = s && s.domain && DOMAIN_MERGE_TILE[s.domain];
    if (t && !order.includes(t)) order.push(t);
  });
  const hasPricing = order.includes("pricing");
  const out = order.filter((t) => t !== "pricing");
  if (hasPricing) out.push("pricing");
  return out.slice(0, 5);
}

// Widest stay-date range spanning the given sources (for the combined view).
export function widestRange(sources) {
  let from = null, to = null;
  (sources || []).forEach((s) => {
    const p = (s && s.params) || {};
    if (p.date_from && (!from || p.date_from < from)) from = p.date_from;
    if (p.date_to && (!to || p.date_to > to)) to = p.date_to;
  });
  return { from, to };
}
